import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowUpRight, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { trackEvent, getTrackingMeta, trackFormStart, trackQuoteSubmit, trackPlanSelected, trackDiyReferralClick, trackWhatsappQuoteSubmitted, SERVICE } from "@/lib/tracking";
import { WA_PLANS } from "@/components/whatsapp/WhatsappPricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROBLEM_OPTIONS = [
  "We reply too slowly to enquiries",
  "We need to capture leads from WhatsApp",
  "We need to qualify leads before speaking to them",
  "We answer the same questions repeatedly",
  "We need automated follow-up",
  "We need appointment or booking reminders",
  "We need customer support automation",
  "We want to send promotions or broadcasts",
  "We need WhatsApp connected to paid ads",
  "We need leads sent to Google Sheets or CRM",
  "We need AI-assisted replies",
  "Other",
];

const USE_CASE_OPTIONS = [
  "Lead generation",
  "Customer support",
  "Bookings and appointments",
  "eCommerce sales",
  "Paid ads follow-up",
  "Promotions and broadcasts",
  "Internal sales team support",
  "Not sure yet",
];

const TIMING_OPTIONS = [
  "Within 5 days", "Within 14 days", "By the end of this month", "Next month", "Not sure yet",
];

const BUDGET_OPTIONS = [
  "Less than R5,000", "R5,000 - R10,000", "R10,000 - R25,000", "R25,000 - R50,000", "More than R50,000", "Not sure yet",
];

const SUPPORT_OPTIONS = [
  { value: "diy", label: "DIY with Chatfuel" },
  { value: "dfy", label: "Done-for-You Setup - from R10,850 once-off" },
  { value: "dwy", label: "Done-with-You Enablement - from R4,850 p/m" },
  { value: "not_sure", label: "Not sure yet" },
];

const PLAN_TO_SUPPORT = { diy: "diy", dwy: "dwy", dfy: "dfy" };

const CHATFUEL_REFERRAL = "https://chatfuel.com/?fpr=thato21";

const FIELD_BASE =
  "bg-white border border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:border-[#25d366] focus-visible:ring-2 focus-visible:ring-[#25d366]/20 rounded-lg h-12 transition-shadow";

export default function WhatsappForm({ selectedPlan, setSelectedPlan }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", company_name: "", website_url: "",
    business_email: "", mobile_number: "",
    problems: [], problem_other: "",
    use_case: "", go_live_timing: "", support_option: "", monthly_budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const startedRef = useRef(false);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (selectedPlan && PLAN_TO_SUPPORT[selectedPlan]) {
      setForm((f) => ({ ...f, support_option: PLAN_TO_SUPPORT[selectedPlan] }));
    }
  }, [selectedPlan]);

  const handleField = (k, v) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("form_start", { service: "whatsapp" });
      trackFormStart({ service: SERVICE.whatsapp, formName: "whatsapp_automation_quote_form" });
    }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toggleProblem = (opt) =>
    handleField("problems", form.problems.includes(opt) ? form.problems.filter((p) => p !== opt) : [...form.problems, opt]);

  const validate = () => {
    if (!form.first_name.trim()) return "First name is required.";
    if (!form.last_name.trim()) return "Last name is required.";
    if (!form.company_name.trim()) return "Company name is required.";
    if (!form.business_email.trim() || !/.+@.+\..+/.test(form.business_email)) return "A valid business email is required.";
    if (!form.mobile_number.trim()) return "Mobile number is required.";
    if (form.problems.length === 0) return "Please select at least one problem.";
    if (form.problems.includes("Other") && !form.problem_other.trim()) return "Please describe the problem.";
    if (!form.use_case) return "Please select a use case.";
    if (!form.go_live_timing) return "Please tell us when you want to go live.";
    if (!form.support_option) return "Please select a support option.";
    if (!form.monthly_budget) return "Please choose a budget.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inFlightRef.current) return;
    const err = validate();
    if (err) { toast.error(err); return; }

    const selectedAddons = [];

    const payload = {
      service_type: "whatsapp_automation",
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      company_name: form.company_name.trim(),
      website_url: form.website_url.trim() || null,
      business_email: form.business_email.trim(),
      mobile_number: form.mobile_number.trim(),
      problems: form.problems,
      problem_other: form.problems.includes("Other") ? form.problem_other.trim() : null,
      use_case: form.use_case,
      go_live_timing: form.go_live_timing,
      support_option: form.support_option,
      monthly_budget: form.monthly_budget,
      addons: selectedAddons,
      message: form.message.trim() || null,
      tracking: getTrackingMeta(),
    };

    try {
      inFlightRef.current = true;
      setSubmitting(true);
      const res = await axios.post(`${API}/quote`, payload);
      trackWhatsappQuoteSubmitted({
        leadId: res.data?.id,
        planId: form.support_option,
        budget: form.monthly_budget,
        goLive: form.go_live_timing,
      });
      trackEvent("form_submit", { service: "whatsapp", plan: form.support_option, addons: selectedAddons, budget: form.monthly_budget });
      trackQuoteSubmit({
        service: SERVICE.whatsapp,
        planId: form.support_option,
        addonIds: [],
        budget: form.monthly_budget,
        goLive: form.go_live_timing,
      });
      setSubmitted(true);
      toast.success("WhatsApp automation request received");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
      toast.error("Could not submit your request. Please try again.");
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="request-quote" data-testid="wa-quote-form-success" className="relative py-24 sm:py-32 bg-white">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <div className="h-16 w-16 rounded-full bg-[#25d366]/15 border border-[#25d366]/40 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-8 w-8 text-[#25d366]" />
          </div>
          <h2 className="font-display mt-6 text-3xl sm:text-5xl font-black tracking-tight text-zinc-900">
            Thank you. Your WhatsApp Automation enquiry has been received.
          </h2>
          <p className="mt-4 text-zinc-600 leading-relaxed text-lg">
            Our team will review the information and contact you about the next steps.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="request-quote" data-testid="wa-quote-form-section" className="relative py-24 sm:py-32 bg-gradient-to-b from-zinc-50 to-white">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <div className="text-center mb-10">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#128c4b] font-mono mb-3 font-bold">WhatsApp implementation quote</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            Request a WhatsApp Automation Quote
          </h2>
          <p data-testid="wa-quote-intro" className="mt-5 text-zinc-600 text-lg leading-relaxed max-w-2xl mx-auto">
            Tell us about your business and what you want to automate. Rekisa will review your requirements and recommend an
            appropriate implementation approach.
          </p>
        </div>

        <form onSubmit={handleSubmit} data-testid="wa-quote-form"
              className="rounded-3xl border border-zinc-200 bg-white p-6 sm:p-10 space-y-7 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.15)]">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="First Name" required>
              <Input data-testid="wa-field-first-name" aria-label="First name" className={FIELD_BASE} value={form.first_name}
                     onChange={(e) => handleField("first_name", e.target.value)} autoComplete="given-name" />
            </Field>
            <Field label="Last Name" required>
              <Input data-testid="wa-field-last-name" aria-label="Last name" className={FIELD_BASE} value={form.last_name}
                     onChange={(e) => handleField("last_name", e.target.value)} autoComplete="family-name" />
            </Field>
          </div>

          <Field label="Company Name" required>
            <Input data-testid="wa-field-company" aria-label="Company name" className={FIELD_BASE} value={form.company_name}
                   onChange={(e) => handleField("company_name", e.target.value)} autoComplete="organization" />
          </Field>

          <Field label="Website URL">
            <Input data-testid="wa-field-website" aria-label="Website URL" className={FIELD_BASE} placeholder="https://yourwebsite.co.za" type="url"
                   value={form.website_url} onChange={(e) => handleField("website_url", e.target.value)} autoComplete="url" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Business Email" required>
              <Input data-testid="wa-field-email" aria-label="Business email" className={FIELD_BASE} type="email" value={form.business_email}
                     onChange={(e) => handleField("business_email", e.target.value)} autoComplete="email" />
            </Field>
            <Field label="Mobile Number" required>
              <Input data-testid="wa-field-mobile" aria-label="Mobile number" className={FIELD_BASE} type="tel" inputMode="tel" value={form.mobile_number}
                     onChange={(e) => handleField("mobile_number", e.target.value)} autoComplete="tel" />
            </Field>
          </div>

          <div>
            <FieldHeader label="What WhatsApp problem do you need to solve?" required />
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {PROBLEM_OPTIONS.map((opt) => {
                const checked = form.problems.includes(opt);
                return (
                  <label key={opt} htmlFor={`wa-problem-${opt}`}
                         data-testid={`wa-field-problem-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                         className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${
                           checked ? "border-[#25d366] bg-[#25d366]/10" : "border-zinc-200 bg-white hover:border-zinc-300"
                         }`}>
                    <Checkbox id={`wa-problem-${opt}`} checked={checked} onCheckedChange={() => toggleProblem(opt)}
                              className="mt-0.5 border-zinc-300 data-[state=checked]:bg-[#25d366] data-[state=checked]:border-[#25d366]" />
                    <span className="text-sm text-zinc-800 leading-snug">{opt}</span>
                  </label>
                );
              })}
            </div>
            {form.problems.includes("Other") && (
              <div className="mt-4">
                <Field label="Please describe the problem" required>
                  <Textarea data-testid="wa-field-problem-other-detail" aria-label="Describe your other problem"
                            className={`${FIELD_BASE} min-h-[100px] py-3 h-auto`} value={form.problem_other}
                            onChange={(e) => handleField("problem_other", e.target.value)} />
                </Field>
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Which use case matters most?" required>
              <Select value={form.use_case} onValueChange={(v) => handleField("use_case", v)}>
                <SelectTrigger data-testid="wa-field-usecase" aria-label="Main automation requirement" className={`${FIELD_BASE} text-left`}>
                  <SelectValue placeholder="Choose a use case" />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200 text-zinc-900">
                  {USE_CASE_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-zinc-100">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="When do you want to go live?" required>
              <Select value={form.go_live_timing} onValueChange={(v) => handleField("go_live_timing", v)}>
                <SelectTrigger data-testid="wa-field-timing" aria-label="Implementation timeframe" className={`${FIELD_BASE} text-left`}>
                  <SelectValue placeholder="Choose a timing" />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200 text-zinc-900">
                  {TIMING_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-zinc-100">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Which support option are you interested in?" required>
              <Select value={form.support_option} onValueChange={(v) => {
                handleField("support_option", v);
                if (PLAN_TO_SUPPORT[v]) setSelectedPlan(v);
                const eventMap = {
                  diy: "pricing_plan_selected_diy_chatfuel",
                  dwy: "pricing_plan_selected_dwy",
                  dfy: "pricing_plan_selected_dfy_setup",
                };
                if (eventMap[v]) trackEvent(eventMap[v], { plan: v, source: "form", service: "whatsapp" });
                trackPlanSelected({ service: SERVICE.whatsapp, planId: v });
              }}>
                <SelectTrigger data-testid="wa-field-support" aria-label="Support option" className={`${FIELD_BASE} text-left`}>
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200 text-zinc-900">
                  {SUPPORT_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value} className="focus:bg-zinc-100">{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.support_option === "diy" && (
                <div
                  data-testid="wa-field-support-diy-helper"
                  className="mt-3 rounded-xl border border-[#9333ea]/30 bg-[#9333ea]/[0.06] p-4"
                >
                  <p className="text-sm text-zinc-800 leading-relaxed">
                    You can start directly on Chatfuel using Rekisa's partner link.
                  </p>
                  <a
                    href={CHATFUEL_REFERRAL}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    onClick={() => {
                      trackEvent("outbound_referral_click_chatfuel", { source: "form_helper" });
                      trackDiyReferralClick({
                        service: SERVICE.whatsapp,
                        partnerName: "Chatfuel",
                        referralUrl: CHATFUEL_REFERRAL,
                      });
                    }}
                    data-testid="wa-field-support-diy-button"
                    className="inline-flex items-center gap-1.5 mt-3 rounded-full bg-zinc-900 hover:bg-black text-white px-5 py-2.5 text-sm font-semibold transition-colors"
                  >
                    Start DIY on Chatfuel
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              )}
            </Field>
            <Field label="What budget can you start with?" required>
              <Select value={form.monthly_budget} onValueChange={(v) => handleField("monthly_budget", v)}>
                <SelectTrigger data-testid="wa-field-budget" aria-label="Budget range" className={`${FIELD_BASE} text-left`}>
                  <SelectValue placeholder="Choose a budget" />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200 text-zinc-900">
                  {BUDGET_OPTIONS.map((b) => (
                    <SelectItem key={b} value={b} className="focus:bg-zinc-100">{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Message">
            <Textarea data-testid="wa-field-message" aria-label="Additional information"
                      className={`${FIELD_BASE} min-h-[140px] py-3 h-auto`}
                      placeholder="Tell us what you want the WhatsApp automation to do."
                      value={form.message} onChange={(e) => handleField("message", e.target.value)} />
          </Field>

          <div className="pt-2">
            <Button type="submit" data-testid="wa-quote-form-submit" disabled={submitting}
                    className="w-full bg-[#25d366] hover:bg-[#1ebe5d] text-[#04220f] rounded-full h-14 text-base font-semibold shadow-[0_8px_24px_rgba(37,211,102,0.4)] hover:shadow-[0_12px_32px_rgba(37,211,102,0.5)] transition-all disabled:opacity-70">
              {submitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting...</>)
                          : (<>Request a Quote <ArrowUpRight className="ml-1 h-5 w-5" /></>)}
            </Button>
            <p className="mt-4 text-center text-xs text-zinc-500">
              We respect your privacy. Your details are only used to respond to your quote request.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}

function FieldHeader({ label, required }) {
  return (
    <Label className="text-sm font-semibold text-zinc-800">
      {label}{required && <span className="text-[#ea4335] ml-1">*</span>}
    </Label>
  );
}

function Field({ label, required, children }) {
  return (<div className="space-y-2"><FieldHeader label={label} required={required} />{children}</div>);
}
